import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { getMediumImageUrl } from 'src/Constants/functions'
import { APP } from 'src/Configs/App'

export type SpotifyTrack = {
  id: string
  trackName: string
  trackAuthor: string
  trackImage: string
  trackDuration: number
  trackHref: string
  previewUrl: string
  resourceSpotifyUrl: string
  artistSpotifyId: string
  artists: {
    external_urls: {
      spotify: string
    }
    href: string
    id: string
    name: string
    type: string
    uri: string
  }[]
  trackCoverUrls: SpotifyTrackCoverUrls[]
}

type SpotifyTrackArtists = {
  externalSpotifyUrl: string
  href: string
  id: string
  name: string
  type: string
  uri: string
}

type SpotifyTrackCoverUrls = {
  url: string
  height: number
  width: number
}

type SpotifyTrackNestResponse = {
  artists: SpotifyTrackArtists[]
  id: string
  name: string
  previewUrl: string
  resourceSpotifyUrl: string
  trackCoverUrls: SpotifyTrackCoverUrls[]
  trackDuration: number
}
export const spotifyApi = createApi({
  baseQuery: fetchBaseQuery(),
  reducerPath: 'spotifyApi',
  tagTypes: ['SpotifyTracks'],
  endpoints: (builder) => ({
    clearSpotifyTracks: builder.mutation<never[], void>({
      queryFn() {
        return { data: [] }
      },
      onQueryStarted: (_, { dispatch }) => {
        dispatch(spotifyApi.util.resetApiState())
      },
      invalidatesTags: ['SpotifyTracks'],
    }),

    getSpotifyTrackById: builder.query<SpotifyTrack, string>({
      query: (id) => ({
        url: `${APP.TIKTOK_SERVER}/public/spotify-tracks/${id}`,
      }),
      transformResponse: (response: SpotifyTrackNestResponse) => ({
        id: response.id,
        trackName: response.name,
        trackAuthor: response.artists.map((artist) => artist.name).join(', '),
        trackImage: getMediumImageUrl(response.trackCoverUrls),
        trackDuration: response.trackDuration,
        trackHref: `https://api.spotify.com/v1/tracks/${response.id}`,
        resourceSpotifyUrl: response.resourceSpotifyUrl,
        previewUrl: response.previewUrl,
        artistSpotifyId: response.artists?.[0].id,
        artists: response.artists.map((artist) => ({
          ...artist,
          external_urls: {
            spotify: artist.externalSpotifyUrl,
          },
        })),
        trackCoverUrls: response.trackCoverUrls,
      }),
      providesTags: ['SpotifyTracks'],
    }),
    getSpotifyTracksBySearch: builder.query<SpotifyTrack[], string>({
      query: (search) => ({
        url: `${APP.TIKTOK_SERVER}/public/spotify-tracks/search?search=${search}&limit=20`,
      }),
      providesTags: ['SpotifyTracks'],
      transformResponse: (response: SpotifyTrackNestResponse[]) =>
        response.map((track) => ({
          id: track.id,
          trackName: track.name,
          trackAuthor: track.artists.map((artist) => artist.name).join(', '),
          trackImage: getMediumImageUrl(track.trackCoverUrls),
          trackDuration: track.trackDuration,
          trackHref: `https://api.spotify.com/v1/tracks/${track.id}`,
          previewUrl: track.previewUrl,
          resourceSpotifyUrl: track.resourceSpotifyUrl,
          artistSpotifyId: track.artists?.[0].id,
          artists: track.artists.map((artist) => ({
            ...artist,
            external_urls: {
              spotify: artist.externalSpotifyUrl,
            },
          })),
          trackCoverUrls: track.trackCoverUrls,
        })),
    }),
  }),
})

export const {
  useGetSpotifyTrackByIdQuery,
  useLazyGetSpotifyTrackByIdQuery,
  useLazyGetSpotifyTracksBySearchQuery,
  useClearSpotifyTracksMutation,
} = spotifyApi
